import withDisclaimer from "@/hoc/withDisclaimer";
import { useParams } from "react-router";
import AllocationBlueprint from "./AllocationBlueprint";
import NoUPMessage from "@/components/NoUPMessage";
import AllocationBlueprintSkeleton from "./AllocationBlueprintSkeleton";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import useHeaderChange from "@/hooks/useHeaderChange";
import npvSlice from "@/redux/npv/npv-slice";
import NoDataMessage from "@/components/NoDataMessage";
import { calculateAge } from "../UP/up-utils";
import { NetPresentValue } from "../NPV/npv-utils";
import { HouseholdDataType } from "@/types/enums";
import LockComponent from "@/components/LockComponent";
import { checkClientsBirthdates } from "@/helper/helper";
import { NoContactBirthDateMessage } from "../HouseholdContact";
import { useAppSelector } from "@/hooks/useAppSelector";

function AllocationBlueprintWrapper() {
  const { id } = useParams();
  const { settings } = useAppSelector((state) => state.npv);
  const { upData, upLoading, upError, upIsError, upRefetching, upLoaded } = useHeaderChange(
    id!,
    "Allocation Blueprint"
  );
  const [hasData, setHasData] = useState({
    ab: true,
    fab: false,
  });

  useEffect(() => {
    if (upIsError)
      toast.error(
        upError instanceof AxiosError
          ? upError?.message
          : "Failed to fetch Unique Possibilities data"
      );
  }, [upError, upIsError]);

  if (upLoading || upRefetching) return <AllocationBlueprintSkeleton />;
  if (upIsError) return <span>Failed to fetch unique possibilities</span>;
  if (checkClientsBirthdates(upData?.household)) {
    return <NoContactBirthDateMessage household={upData?.household} />;
  }
  if (!upData?.data?.length) return <NoUPMessage />;
  if (!hasData.ab && !hasData.fab) return <NoDataMessage />;

  const age1 = calculateAge(
    upData?.household?.primaryContact?.birthDate.toString() ?? "",
    0
  )?.value;
  const age2 = calculateAge(
    upData?.household?.secondaryContact?.birthDate.toString() ?? "",
    0
  )?.value;

  const { gridSet } = JSON.parse(upData?.data);
  const npvSettings = upData?.npvSettings ? JSON.parse(upData.npvSettings as string) : settings;
  const { npv } = NetPresentValue.calcHelpers(npvSettings, gridSet, age1, age2);

  const npvSolution =
    upData.household?.lockedNPV ?? Math.max(0, npv[npvSettings?.npvAge - 5]?.npv ?? 0);
  const abFixedIncome = upData?.household?.clientAdvisorSetABFixedIncome ?? npvSolution;

  const showFab =
    (upLoaded && JSON.parse(upData?.npvSettings as string)?.yearsToRetire > 0) ??
    npvSlice.getInitialState().settings.yearsToRetire > 0;

  return (
    <>
      <div className="flex gap-4 pb-4 print:hidden">
        <LockComponent
          dataType={HouseholdDataType.NPV}
          initialValue={Math.max(0, npv[npvSettings?.npvAge - 5]?.npv ?? 0)}
          lockedInValue={upData?.household?.lockedNPV}
          lockText="NPV Solution"
          lockTooltipPosition={{ my: "left", at: "center", offset: { x: 20, y: 0 } }}
          externalCode={upData?.household?.externalCode as string}
        />
      </div>
      <div
        className={`flex gap-8 screen:min-h-[calc(100dvh-350px)] ${showFab ? "print:flex-col" : ""}`}
      >
        <AllocationBlueprint
          upData={upData}
          initFixedIncome={abFixedIncome}
          fabVisible={showFab}
          setHasData={setHasData}
        />
        {showFab ? (
          <AllocationBlueprint
            upData={upData}
            initFixedIncome={npvSolution}
            isFAB
            fabVisible={showFab}
            setHasData={setHasData}
          />
        ) : null}
      </div>
    </>
  );
}

const ABWithDisclaimer = withDisclaimer(AllocationBlueprintWrapper);

export default ABWithDisclaimer;
